import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createCheckboxDefaults } from './checkbox-defaults.factory.js';
import type { CheckboxIcon } from './checkbox-icons.config.js';

/**
 * Subscribes to `place/checkbox` UiActions and materialises a new checkbox:
 * builds the CheckboxObject via `createCheckboxDefaults`, pushes an
 * `AddObjectCommand` onto history, then re-emits `selection/set` and
 * `edit/enter-plain {field:'label'}` for the new id (TR6, TR7).
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * FC4: never publishes onto `selection$`/`tool$`/`viewport$` directly; downstream
 * adapters translate `selection/set` into a `selection$` write.
 *
 * FC17: dispatches the dedicated `edit/enter-plain` discriminator (with
 * `field:'label'`) — never the sticky `edit/enter-sticky` action — so the
 * `PlainEditController` and `StickyEditorController` cannot cross-talk.
 */
export class CheckboxPlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/checkbox' }> =>
                        a.type === 'place/checkbox',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[CheckboxPlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/checkbox' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        // Stefan, 2026-09-03 — "choose properties before drawing", see the
        // identical comment in rectangle-placement.controller.ts.
        //
        // Review-Lauf 01 (2026-09-03): the staged-overrides merge now happens
        // INSIDE `createCheckboxDefaults` itself (its `options` 4th
        // parameter), exactly like every other tool — not as a
        // post-construction spread here. See that factory's own doc comment
        // for why the earlier "no room for an options parameter" reasoning
        // was wrong, and how the post-construction spread had silently
        // defeated `freezeInDev()` for every checkbox placement.
        const staged = this.#bus.stagedToolProperties$.value.checkbox;
        const checkbox = createCheckboxDefaults(
            scale,
            { x: action.worldX, y: action.worldY },
            null,
            staged && {
                color: staged['color'] as string | undefined,
                icon: staged['icon'] as CheckboxIcon | undefined,
                sizeKey: staged['sizeKey'] as 'S' | 'M' | 'L' | undefined,
            },
        );

        const cmd = new AddObjectCommand(checkbox);
        this.#history.push(cmd, this.#state);

        const newId = objectId(checkbox.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
        this.#bus.uiActions$.next({ type: 'edit/enter-plain', id: newId, field: 'label' });
    }
}
